import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useMediaQuery } from 'react-responsive';
import { Redirect } from 'react-router-dom';

import { SCREEN_ID_APPLY } from 'client/constants/screenIds';

import { MEDIA_QUERY_SMALL_SCREEN } from 'client/constants/mediaQueries';

import { hasRoleAnonymous, hasRolePartner } from 'common/userRoles';

import { numberWithCommas, numberFixedTwoDecimals } from 'common/formatters';

import { isListingTenantScreening, isSunroomListing } from 'common/listingHelpers';

import { LISTING_STATUS_ARCHIVED } from 'common/listingConstants';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';

import SrStepper, { SR_STEPPER_THEME_GREYCLIFF } from 'client/components/SrStepper';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrIcon from 'client/components/SrIcon';
import { BUTTON_THEME_GREEN_ADOBE } from 'client/components/ButtonDeprecated';
import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';
import SrModalV2, {
  MODAL_THEME_FIXED_FOOTER_BUTTON,
  MODAL_THEME_FIXED_FOOTER_BUTTON_SLIDE_UP,
} from 'client/components/SrModalV2/SrModalV2';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';

import branch from 'client/core/branch';
import PartnerActions from 'client/actions/Partner';
import ListingActions from 'client/actions/Listing';
import HouseholdActions from 'client/actions/Household';
import HouseholdApplicationActions from 'client/actions/HouseholdApplication';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrCard from 'client/components/SrCard/SrCard';

const ApplyToRentModal = ({ currentUser, listing, history, dispatch }) => {
  const isSmallScreen = useMediaQuery({
    query: MEDIA_QUERY_SMALL_SCREEN,
  });

  const onStartApplying = () => {
    if (hasRoleAnonymous(currentUser.roles)) {
      history.push(`/start?startPrequal=true&listingId=${listing.id}`);
      return;
    }

    dispatch(HouseholdActions.startPrequal, {
      userId: currentUser.id,
      listingId: listing.id,
    });
  };

  return (
    <SrModalV2
      theme={
        isSmallScreen ? MODAL_THEME_FIXED_FOOTER_BUTTON_SLIDE_UP : MODAL_THEME_FIXED_FOOTER_BUTTON
      }
      contentMaxWidth={!isSmallScreen ? '450px' : null}
      contentPadding="40px 0px 100px"
      contentBorderRadius={isSmallScreen ? null : '24px'}
      contentHeight={isSmallScreen ? null : 'unset'}
      showCloseButton={false}
      closeOnBackgroundClick
    >
      <SrBox margin="4px 15px 14px 15px">
        <SrFlex align="center" direction="col">
          <SrIcon className="sr-logo" />
          <SrBox height="16px" />
          <SrIcon className="apply-to-listing" />
          <SrText
            color="blueGreycliff"
            font="greycliff"
            fontSize="36px"
            lineHeight="36px"
            weight="800"
            paddingTop={isSmallScreen ? '10px' : '31px'}
          >
            Apply to Rent
          </SrText>
          <SrText
            color="blueGreycliff"
            font="greycliff"
            fontSize="21px"
            lineHeight="36px"
            weight="800"
            paddingTop="16px"
          >
            {listing.addressStreetDisplay}
          </SrText>
        </SrFlex>
        <SrBox padding="27px 0 40px">
          <SrStepper
            theme={SR_STEPPER_THEME_GREYCLIFF}
            steps={[
              {
                title: 'Prequalify',
              },
              {
                title: 'Apply',
              },
              {
                title: 'Approved',
              },
            ]}
            showNumberStep
          />
        </SrBox>
        <SrButton hasBoxShadow theme={SR_BUTTON_THEME_ADOBE_GREEN} onClick={onStartApplying}>
          Start Applying
        </SrButton>
      </SrBox>
    </SrModalV2>
  );
};

const getReasons = ({ requirements }) => {
  const reasons = requirements
    .filter(({ isQualified }) => !isQualified)
    .map(({ id, title, value, expectedValue }) => {
      let reasonText = null;

      switch (id) {
        case 'creditScore': {
          reasonText = `Credit Score: ${numberWithCommas(value)}`;
          break;
        }
        case 'monthlyIncome': {
          const incomeAmount = numberFixedTwoDecimals(value);
          const incomeInputted = numberWithCommas(incomeAmount);
          const incomeExpected = numberWithCommas(expectedValue);

          reasonText = `Monthly Household Income: $${incomeInputted} (required to be $${incomeExpected})`;
          break;
        }
        case 'moveInDate': {
          const dateInputted = moment(value).utc().format('MMM D');
          const moveInRageExpected = [
            moment(expectedValue.startDate).utc().format('MMM D'),
            moment(expectedValue.endDate).utc().format('MMM D'),
          ].join(' - ');

          reasonText = `Move-In: ${dateInputted} (required to be ${moveInRageExpected})`;
          break;
        }
        case 'petBreeds': {
          reasonText = `Dog Breed: ${expectedValue.breedsNotAllowed.join(', ')}`;
          break;
        }
        default:
          reasonText = title;
          break;
      }

      return {
        id,
        reasonText,
      };
    });

  return reasons;
};

const RenterQualificationModal = ({ dispatch, listing, isQualified, requirements }) => {
  let iconUrl = null;
  let headlineDOM = null;
  let messageDOM = null;
  let buttonText = null;
  let buttonHref = null;
  let onButtonClick = null;

  if (isQualified) {
    iconUrl = '/images/checkmark-circle-large-green.svg';
    headlineDOM = (
      <>
        <SrText font="greycliff" weight="800" size="26" lineHeight="36px">
          Congrats,
        </SrText>
        <br />
        <SrText font="greycliff" weight="800" size="26" lineHeight="36px" color="green">
          you qualify to apply!
        </SrText>
      </>
    );
    messageDOM = (
      <SrBox paddingTop="30px">
        <SrText font="greycliff" weight="800" size="21" lineHeight="32px">
          You’ve met the prescreening requirements to rent this home. Finish applying now!
        </SrText>
      </SrBox>
    );
    buttonText = 'continue applying';
    onButtonClick = () => {
      dispatch(HouseholdActions.roommateApplicationStep, {
        listingId: listing.id,
      });
    };
  } else {
    iconUrl = '/images/x-circle-icon-red.svg';
    headlineDOM = (
      <SrBox marginTop="22px">
        <SrText font="greycliff" weight="800" size="26" lineHeight="36px">
          Unfortunately you didn’t qualify for this property.
        </SrText>
      </SrBox>
    );
    const reasons = getReasons({ requirements });
    messageDOM = (
      <SrBox marginTop="18px">
        <SrText font="greycliff" weight="700" size="16" lineHeight="22px">
          Reason:
        </SrText>
        <br />
        <SrBox textAlign="left" padding="4px 0px 10px">
          {reasons.map(({ id, reasonText }) => (
            <SrFlex key={id} paddingTop="14ox">
              <SrBox>
                <img
                  style={{
                    width: '20px',
                    height: '20px',
                  }}
                  src={iconUrl}
                  alt="close"
                />
              </SrBox>
              <SrText
                font="greycliff"
                weight="800"
                size="16"
                lineHeight="19px"
                color="red"
                paddingLeft="11px"
              >
                {reasonText}
              </SrText>
            </SrFlex>
          ))}
        </SrBox>
      </SrBox>
    );
    buttonText = 'See rentals I qualify for';
    buttonHref = '/feed';
  }

  return (
    <SrSimpleMessageModal
      iconUrl={iconUrl}
      headline={headlineDOM}
      message={messageDOM}
      buttonText={buttonText}
      buttonTheme={BUTTON_THEME_GREEN_ADOBE}
      onButtonClick={onButtonClick}
      buttonHref={buttonHref}
      theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
      maxWidth={475}
    />
  );
};

const ListingArchived = () => {
  return (
    <SrBox width="100vw" height="100vh">
      <SrPartnerPageV2
        onBack={null}
        navbarImage="/images/sunroom-logo-yellow.svg"
        padding="0"
        contentPadding="0"
        maxWidth="unset"
        navbarMaxWidth="1180px"
        currentUser={null}
      >
        <SrFlex
          width="100vw"
          height="calc(100vh - 67px)"
          justify="center"
          align="center"
          direction="col"
        >
          <SrCard maxWidth="375px" height="331px" padding="40px">
            <SrFlex direction="col" align="center">
              <SrBox marginBottom="32px">
                <img
                  src="/images/sunroom-sad.svg"
                  width="152px"
                  height="152px"
                  alt="Sunroom logo"
                />
              </SrBox>
              <SrText
                color="blueGreycliff"
                font="greycliff"
                weight="800"
                fontSize="26px"
                lineHeight="33px"
                align="center"
              >
                We're sorry! This listing is no longer active
              </SrText>
            </SrFlex>
          </SrCard>
        </SrFlex>
      </SrPartnerPageV2>
    </SrBox>
  );
};

@withTransitionHook
@withScreenId(SCREEN_ID_APPLY)
@withScrollToTop
@branch(({ match }) => ({
  currentUser: ['currentUser'],
  listing: ['listing'],
  renterQualification: ['household', 'renterQualificationsByListingId', match.params.listingId],
  currentPartner: ['partners', 'currentPartner'],
}))
@branch(({ currentUser }) => {
  if (!currentUser.id) {
    return {};
  }
  return {
    userHouseholdApplications: ['household', 'applicationsByUserId', currentUser.id],
  };
})
class ApplyPage extends Component {
  static fetchData(match, inject) {
    const listingId = parseInt(match.params.listingId, 10);
    const currentUser = inject.state.get(['currentUser']);

    return Promise.all([
      ListingActions.getById(inject, listingId),
      !hasRoleAnonymous(currentUser.roles) &&
        HouseholdActions.getRenterListingQualification(inject, {
          userId: currentUser.id,
          listingId,
        }),
      !hasRoleAnonymous(currentUser.roles) &&
        HouseholdApplicationActions.getHouseholdApplicationsByUserId(inject, {
          // eslint-disable-line max-len
          userId: currentUser.id,
        }),
    ]).then(([listing]) => {
      if (listing?.partnerId) {
        return PartnerActions.getById(inject, {
          partnerId: listing.partnerId,
        });
      }
    });
  }

  render() {
    const {
      location,
      history,
      listing,
      currentUser,
      renterQualification,
      dispatch,
      isTransitioning,
      currentPartner,
      userHouseholdApplications,
    } = this.props;

    if (isTransitioning) {
      return null;
    }

    if (!listing) {
      return <Redirect to="/feed" />;
    }

    if (isListingTenantScreening(listing) && listing.status === LISTING_STATUS_ARCHIVED) {
      return <ListingArchived />;
    }

    if (!isSunroomListing(listing)) {
      return <Redirect to={`/listing/${listing.id}`} />;
    }

    if (hasRolePartner(currentUser.roles) && currentPartner) {
      if (currentPartner.id === listing.partnerId) {
        return <Redirect to={`/partner/${currentPartner.slug}/listing/${listing.id}`} />;
      }

      return <Redirect to={`/partner/${currentPartner.slug}`} />;
    }

    if (listing && userHouseholdApplications?.length) {
      const household = userHouseholdApplications.find(
        ({ householdListing }) => householdListing.listingId === listing.id,
      );

      if (household) {
        return <Redirect to={`/profile?viewingListingId=${listing.id}`} />;
      }
    }

    if (renterQualification && location.search.includes('showQualifications')) {
      return (
        <RenterQualificationModal
          dispatch={dispatch}
          listing={listing}
          isQualified={renterQualification.isQualified}
          requirements={renterQualification.requirements}
        />
      );
    }

    return (
      <ApplyToRentModal
        currentUser={currentUser}
        listing={listing}
        history={history}
        dispatch={dispatch}
      />
    );
  }
}

ApplyPage.displayName = 'ApplyPage';

ApplyPage.propTypes = {
  listing: PropTypes.object,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  renterQualification: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  isTransitioning: PropTypes.bool.isRequired,
  currentPartner: PropTypes.object.isRequired,
};

export default ApplyPage;
